import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon, ChevronRight, Maximize2, ArrowUpRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
type Persona = {
  id: string;
  role: string;
  name: string;
  headline: string;
  doing: string;
  tools: string[];
  examples: string[];
  outcome: string;
  taskletOutcome: string;
  color: string;
  image?: string;
  activeImage?: string;
};
const GROUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/9XRofhQAk118LHvtkbKC4f/Group_60.png';
const PERSONAS: Persona[] = [{
  id: 'sales',
  role: 'Head of Sales',
  name: 'Sofia',
  image: 'https://cdn.magicpatterns.com/uploads/9jwxkRExcCBYEhh1GCsye3/Rectangle_(5).png',
  headline: 'Every lead followed up. Every deal moving.',
  doing: 'Reviving 4 stale deals with tailored outreach',
  tools: ['salesforce', 'gmail', 'slack'],
  examples: ['Write follow-ups for everyone I met this week', 'Tell me which deals are about to go cold', 'Update the CRM from my call notes'],
  outcome: 'So your Head of Sales spends their time closing, not updating the CRM.',
  taskletOutcome: 'Your Giga Head of Sales updates the CRM, so you can spend your time closing.',
  color: 'from-amber-500/20'
}, {
  id: 'cto',
  role: 'CTO',
  name: 'Aaron',
  image: 'https://cdn.magicpatterns.com/uploads/k9zuEY5MuYdrGKsbqc2qAT/Rectangle_(6).png',
  headline: 'Your roadmap moves while the busywork handles itself.',
  doing: 'Triaging 14 Sentry errors into Linear tickets',
  tools: ['github', 'linear', 'slack'],
  examples: ["Turn today's bug reports into tickets", 'Summarize what the team shipped this week', 'Write the postmortem from the incident thread'],
  outcome: 'So your CTO ships architecture, not issue tickets.',
  taskletOutcome: 'Your Giga CTO handles issue tickets, so you can ship architecture.',
  color: 'from-emerald-500/20'
}, {
  id: 'cmo',
  role: 'CMO',
  name: 'Devin',
  image: 'https://cdn.magicpatterns.com/uploads/g6BkGzwa9CwdyEJ6YT8GbH/Rectangle_(2).png',
  headline: 'Every dollar of spend earning its keep.',
  doing: 'Pausing ad sets under 0.8× ROAS in Meta',
  tools: ['google-analytics', 'twitter', 'tiktok'],
  examples: ['Show me which ads are wasting budget', "Recap how last week's campaigns did", 'Break down what each channel costs us'],
  outcome: 'So your CMO drives growth, not wrangling spreadsheets.',
  taskletOutcome: 'Your Giga CMO wrangles spreadsheets, so you can drive growth.',
  color: 'from-purple-500/20'
}, {
  id: 'content',
  role: 'Head of Content',
  name: 'Theo',
  headline: 'A content engine that never runs dry.',
  doing: 'Drafting a week of on-brand social posts',
  tools: ['twitter', 'tiktok', 'notion'],
  examples: ['Turn this launch into a week of posts', 'Rewrite this in our brand voice', 'Repurpose the webinar into a thread'],
  outcome: 'So your Head of Content crafts the story, not the busywork.',
  taskletOutcome: 'Your Giga Head of Content handles the busywork, so you can craft the story.',
  color: 'from-orange-500/40',
  image: 'https://cdn.magicpatterns.com/uploads/5BL2mbWeFKFZH1mcoCyf7Y/Rectangle_(4).png'
}, {
  id: 'designer',
  role: 'Head of Design',
  name: 'Anika',
  image: 'https://cdn.magicpatterns.com/uploads/unXpqjKJHSrjrYP1dypr2x/Rectangle_(3).png',
  headline: 'From brief to polished mockups in minutes.',
  doing: 'Turning the brief into 6 on-brand mockups in Figma',
  tools: ['figma', 'slack', 'notion'],
  examples: ['Turn the brief into a few mockups', 'Find inconsistencies in our design system', 'Round up the feedback from Slack'],
  outcome: 'So your Head of Design perfects the experience, not pushing pixels.',
  taskletOutcome: 'Your Giga Head of Design pushes pixels, so you can perfect the experience.',
  color: 'from-rose-500/30'
}, {
  id: 'cfo',
  role: 'CFO',
  name: 'Margot',
  image: 'https://cdn.magicpatterns.com/uploads/sVUmBUgvixiAk6C2Z9XavX/Mask_group_(7).png',
  headline: 'Books that practically close themselves.',
  doing: "Reconciling this month's P&L from Stripe → Xero",
  tools: ['stripe', 'microsoft-excel', 'quickbooks'],
  examples: ["Reconcile this month's numbers", 'Flag invoices that went over budget', 'Build a cash-flow forecast for next quarter'],
  outcome: 'So your CFO models the future, not reconciling the past.',
  taskletOutcome: 'Your Giga CFO reconciles the past, so you can model the future.',
  color: 'from-blue-500/20'
}];
function ToolChips({
  tools
}: {
  tools: string[];
}) {
  return <div className="flex items-center -space-x-1" data-id="element-57">
      {tools.map((tool, idx) => <div key={tool} className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white border border-neutral-200 shadow-sm flex items-center justify-center relative" style={{
      zIndex: 10 - idx
    }} data-id="element-58">
          <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tool}.svg`} alt={tool} className="w-2.5 h-2.5 md:w-3 md:h-3 object-contain" onError={e => {
        ;
        (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
      }} data-id="element-59" />
        </div>)}
    </div>;
}
// A single in-progress task row — pulsing status dot + task + animated progress
// shimmer, so the whole stack reads as concurrent work happening right now.
function LiveTaskRow({
  task,
  index
}: {
  task: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 16
  }} transition={{
    delay: 0.4 + index * 0.1,
    duration: 0.4
  }} className="relative overflow-hidden bg-white/70 rounded-2xl border border-[#EAE4D9] p-3.5 md:p-4 shadow-sm" data-id="element-60">
      <div className="flex items-center gap-3" data-id="element-61">
        <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-62">
          <motion.span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C]" animate={reduce ? {} : {
          opacity: [0.75, 0, 0.75],
          scale: [1, 2.2, 1]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: index * 0.45
        }} data-id="element-63" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-64" />
        </span>
        <span className="text-[15px] md:text-base text-[#2B211A] font-medium leading-snug" data-id="element-65">
          {task}
        </span>
      </div>
      <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#EA580C]/10 overflow-hidden" data-id="element-66">
        <motion.div className="h-full w-1/3 bg-[#EA580C]/70 rounded-full" animate={reduce ? {} : {
        x: ['-100%', '320%']
      }} transition={{
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: index * 0.5
      }} data-id="element-67" />
      </div>
    </motion.div>;
}
// Desktop scatter positions — balanced constellation grid.
// Index order matches PERSONAS: 0 sales, 1 cto, 2 cmo, 3 content, 4 designer, 5 cfo.
const POSITIONS = [{
  top: '76%',
  left: '30%'
}, {
  top: '41%',
  left: '42%'
}, {
  top: '17%',
  left: '60%'
}, {
  top: '63%',
  left: '64%'
}, {
  top: '50%',
  left: '12%'
}, {
  top: '13%',
  left: '18%'
} // 5 cfo
];
// Harmonious size classes per persona
const SIZES = ['w-16 h-16 md:w-32 md:h-32', 'w-16 h-16 md:w-32 md:h-32', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-32 md:h-32' // 5 CFO
];
// Reveal order — instead of popping in top-to-bottom by source order, the
// constellation "lights up" from the center outward so it reads like the team
// assembling rather than a list loading. Indexed by PERSONAS order:
// 0 sales, 1 cto (center), 2 cmo, 3 content, 4 designer, 5 cfo.
const REVEAL_STEP = [1, 0, 2, 1, 3, 2];
export function AICoworkers({
  isTasklet = false
}: {
  isTasklet?: boolean;
}) {
  const copy = useCopy();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const reduce = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const expandedPersona = PERSONAS.find(p => p.id === expandedId);
  return <section className="relative w-full bg-[#FAF8F4] py-12 md:py-24 overflow-hidden" data-id="element-68">
      <div aria-hidden="true" className="dot-matrix z-0" data-id="element-69" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-70">
        {/* Header */}
        <div className="text-left lg:text-center mb-8 md:mb-12" data-id="element-71">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-72">
            One brain, infinite capability
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight text-[#2B211A] leading-[1.1] lg:mx-auto max-w-4xl" data-id="element-73">
            Centralized context across your whole business.
          </h2>
          <p className="mt-5 md:mt-6 text-base md:text-xl text-neutral-500 leading-relaxed max-w-2xl lg:mx-auto" data-id="element-74">
            One AI brain with context across every tool, function, and person.
            That's why it can reconcile books, revive deals, ship code, and
            handle anything else you throw at it.
          </p>
        </div>

        {/* Connected Brain Container */}
        <div className="relative w-full md:min-h-[600px] lg:min-h-[700px] flex flex-col md:block gap-4 md:gap-8 mt-8 md:mt-16" data-id="element-75">
          {/* Mobile connecting spine */}
          <div className="absolute left-8 top-8 bottom-8 w-px border-l border-dashed border-[#D4D0C8] md:hidden z-0" data-id="element-76" />

          {/* Central Brain Node (Desktop only) */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex-col items-center justify-center" data-id="element-77">
            <div className="relative w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-white border border-neutral-200 shadow-xl flex items-center justify-center" data-id="element-78">
              {/* Pulsing rings */}
              <motion.div className="absolute inset-0 rounded-full border border-[#EA580C]/30" animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-79" />
              <motion.div className="absolute inset-0 rounded-full border border-[#EA580C]/20" animate={{
              scale: [1, 2, 1],
              opacity: [0.3, 0, 0.3]
            }} transition={{
              duration: 3,
              delay: 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-80" />

              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#FFF6F0] flex items-center justify-center relative z-10" data-id="element-81">
                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga Brain" className="w-8 h-8 lg:w-10 lg:h-10 object-contain [filter:brightness(0)_saturate(100%)_invert(45%)_sepia(84%)_saturate(1376%)_hue-rotate(349deg)_brightness(94%)_contrast(95%)]" data-id="element-82" />
              </div>
            </div>
            <div className="mt-4 bg-white/80 backdrop-blur-sm px-4 py-1.5 rounded-full border border-neutral-200/60 shadow-sm" data-id="element-83">
              <span className="text-xs font-bold tracking-widest uppercase text-[#EA580C]" data-id="element-84">
                GigaBrain Core
              </span>
            </div>
          </motion.div>

          {/* Connection Lines (Desktop only) */}
          <svg className="hidden md:block absolute inset-0 w-full h-full z-0 pointer-events-none" style={{
          overflow: 'visible'
        }} data-id="element-85">
            {PERSONAS.map((persona, index) => {
            // Calculate path from center to each persona — mirror the same
            // position nudges applied to the heads below so lines land on the
            // portraits, not where they used to sit before the core was added.
            const pos = {
              ...POSITIONS[index]
            };
            if (index === 1) pos.left = '20%'; // CTO moved left
            if (index === 4) pos.top = '25%'; // Designer moved up
            const top = parseFloat(pos.top);
            const left = parseFloat(pos.left);
            // Center point is 50%, 50%
            // Control points for curved lines
            const cp1x = 50 + (left - 50) * 0.5;
            const cp1y = 50;
            const cp2x = left;
            const cp2y = 50 + (top - 50) * 0.5;
            return <g key={`line-${persona.id}`} data-id="element-86">
                  <path d={`M 50% 50% C ${cp1x}% ${cp1y}%, ${cp2x}% ${cp2y}%, ${left}% ${top}%`} fill="none" stroke="#D4D0C8" strokeWidth="1.5" strokeDasharray="4 4" className="opacity-40" data-id="element-87" />
                  <motion.path d={`M 50% 50% C ${cp1x}% ${cp1y}%, ${cp2x}% ${cp2y}%, ${left}% ${top}%`} fill="none" stroke="#EA580C" strokeWidth="2" initial={{
                pathLength: 0,
                opacity: 0
              }} whileInView={{
                pathLength: 1,
                opacity: 0.3
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.5,
                delay: REVEAL_STEP[index] * 0.2,
                ease: 'easeOut'
              }} data-id="element-88" />
                </g>;
          })}
          </svg>

          {PERSONAS.map((persona, index) => {
          // Adjust positions slightly to accommodate the central brain
          const adjustedPos = {
            ...POSITIONS[index]
          };
          if (index === 1) adjustedPos.left = '20%'; // Move CTO left
          if (index === 4) adjustedPos.top = '25%'; // Move Designer up
          const pos = adjustedPos;
          const sizeClass = SIZES[index];
          const floatY = -(5 + index % 3 * 2);
          const floatRot = index % 2 === 0 ? 1.2 : -1.2;
          const floatDur = 4.5 + index % 3 * 0.9;
          const floatDelay = index % 4 * 0.45;
          const popVariants = {
            hidden: reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.6,
              y: 16
            },
            visible: reduce ? {
              opacity: 1,
              transition: {
                duration: 0.4
              }
            } : {
              opacity: 1,
              scale: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }
            }
          };
          return <motion.div key={persona.id} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            staggerChildren: reduce ? 0 : 0.12,
            delayChildren: reduce ? 0 : isDesktop ? REVEAL_STEP[index] * 0.09 : index * 0.08
          }} className="group flex items-center gap-4 md:absolute w-full md:w-auto cursor-pointer outline-none p-0 z-10" style={pos} onFocus={() => setExpandedId(persona.id)} onClick={() => setExpandedId(persona.id)} tabIndex={0} data-id="element-89">
                <motion.div variants={popVariants} className={`relative ${sizeClass} shrink-0 transition-transform duration-300 group-hover:scale-110`} data-id="element-90">
                  <motion.div className="absolute inset-0" animate={reduce ? undefined : {
                y: [0, floatY, 0],
                rotate: [0, floatRot, 0]
              }} transition={reduce ? undefined : {
                duration: floatDur,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: floatDelay
              }} data-id="element-91">
                    <div className="hidden md:block absolute inset-0 rounded-full bg-[#EA580C]/0 group-hover:bg-[#EA580C]/15 blur-xl scale-125 transition-colors duration-300 pointer-events-none" data-id="element-92" />
                    <div className="absolute inset-0 overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-300 bg-white rounded-full shadow-sm border border-neutral-200/50" data-id="element-93">
                      {persona.activeImage || persona.image ? <img src={persona.activeImage || persona.image} alt={persona.name} className="absolute inset-0 w-full h-full object-contain" data-id="element-94" /> : <img src={GROUP_IMAGE} alt={persona.name} className="absolute h-full max-w-none" style={{
                    width: '600%',
                    left: `-${index * 100}%`
                  }} data-id="element-95" />}
                    </div>
                  </motion.div>
                  <div className="hidden md:flex absolute -top-1 -right-1 z-20 items-center justify-center w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[#EA580C] text-white shadow-lg shadow-[#EA580C]/40 ring-2 ring-[#FAF8F4] opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 ease-out pointer-events-none" data-id="element-96">
                    <Maximize2 size={13} strokeWidth={2.75} className="lg:hidden" data-id="element-97" />
                    <Maximize2 size={15} strokeWidth={2.75} className="hidden lg:block" data-id="element-98" />
                  </div>
                </motion.div>

                <motion.div variants={popVariants} className="flex flex-col gap-1.5 flex-1 bg-white/60 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none p-2 md:p-0 rounded-xl md:rounded-none" data-id="element-99">
                  <div className="flex items-center gap-2" data-id="element-100">
                    <span className="font-bold text-[#2B211A] text-sm md:text-base whitespace-nowrap" data-id="element-101">
                      {persona.role}
                    </span>
                    <ToolChips tools={persona.tools} data-id="element-102" />
                    <span className="hidden md:inline-flex items-center gap-0.5 text-[11px] font-bold text-[#EA580C] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 whitespace-nowrap" data-id="element-103">
                      View <ArrowUpRight size={13} strokeWidth={2.75} data-id="element-104" />
                    </span>
                  </div>
                  <div className="flex items-start gap-2 max-w-[200px]" data-id="element-105">
                    <span className="relative flex h-2 w-2 shrink-0 mt-[5px]" data-id="element-106">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-107" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-108" />
                    </span>
                    <p className="font-mono text-[10px] md:text-xs text-neutral-500 leading-relaxed line-clamp-2 md:line-clamp-none" data-id="element-109">
                      {persona.doing}
                    </p>
                  </div>
                </motion.div>

                <div className="md:hidden shrink-0 text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-110">
                  <ChevronRight size={20} data-id="element-111" />
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Expanded Persona Overlay */}
      <AnimatePresence data-id="element-112">
        {expandedPersona && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed inset-0 z-[60] bg-[#F5DDD0] flex md:items-center justify-center overflow-y-auto md:overflow-hidden" data-id="element-113">
            {/* Close affordance — return to the scattered grid to pick another persona */}
            <button type="button" onClick={() => setExpandedId(null)} aria-label="Close persona detail" className="fixed md:absolute top-6 right-6 z-30 flex items-center justify-center rounded-full bg-[#2B211A] hover:bg-[#EA580C] text-white w-12 h-12 shadow-lg shadow-black/10 transition-colors duration-200" data-id="element-114">
              <XIcon size={22} strokeWidth={2.5} data-id="element-115" />
            </button>

            {/* Decorative dashed path (duplicated for overlay) */}
            <div className="absolute inset-0 pointer-events-none z-0 hidden md:block" data-id="element-116">
              <svg className="w-full h-full" viewBox="0 0 1440 1000" preserveAspectRatio="xMidYMid slice" data-id="element-117">
                <path d="M-100,300 C300,100 760,420 1540,160" stroke="#D4D0C8" strokeWidth="1" strokeDasharray="8 8" fill="none" data-id="element-118" />
                <path d="M-100,600 C400,800 880,440 1540,820" stroke="#D4D0C8" strokeWidth="1" strokeDasharray="8 8" fill="none" data-id="element-119" />
              </svg>
            </div>

            <div className="relative z-10 w-full max-w-[1080px] mx-auto px-5 sm:px-6 pt-20 pb-12 md:py-16 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 lg:gap-20 min-h-full" data-id="element-120">
              {/* Left Column: Portrait & Outcome */}
              <div className="w-full md:w-[42%] flex flex-col items-center mt-0" data-id="element-121">
                <motion.div initial={{
              opacity: 0,
              scale: 0.92
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.92
            }} transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1]
            }} className="relative w-32 h-32 sm:w-44 sm:h-44 md:w-60 md:h-60 lg:w-72 lg:h-72 shrink-0 grayscale" data-id="element-122">
                  {/* Inner wrapper handles the continuous head sway, isolated from the entrance transform */}
                  <motion.div className="absolute inset-0" animate={{
                rotate: [-2.5, 2.5, -2.5]
              }} transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} style={{
                transformOrigin: 'top center',
                willChange: 'transform'
              }} data-id="element-123">
                    <img src={expandedPersona.activeImage || expandedPersona.image || GROUP_IMAGE} alt={expandedPersona.name} className="absolute h-full max-w-none" style={!(expandedPersona.activeImage || expandedPersona.image) ? {
                  width: '600%',
                  left: `-${PERSONAS.findIndex(p => p.id === expandedPersona.id) * 100}%`
                } : {
                  width: '100%',
                  objectFit: 'contain'
                }} data-id="element-124" />
                  </motion.div>
                </motion.div>

                {/* Identity */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} transition={{
              delay: 0.18,
              duration: 0.4
            }} className="mt-5 md:mt-7 text-center" data-id="element-125">
                  <p className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-126">
                    {expandedPersona.role}
                  </p>
                </motion.div>

                {/* Connected tools — redesigned as a sleek live status pill */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} transition={{
              delay: 0.4,
              duration: 0.4
            }} className="mt-4 md:mt-5 flex justify-center" data-id="element-127">
                  <div className="inline-flex items-center gap-3 p-1 pr-4 bg-white rounded-full border border-[#EAE4D9] shadow-sm" data-id="element-128">
                    <div className="flex items-center -space-x-1.5" data-id="element-129">
                      {expandedPersona.tools.map((tool, idx) => <div key={tool} className="w-7 h-7 rounded-full bg-[#FAF8F4] border border-white flex items-center justify-center relative shadow-sm" style={{
                    zIndex: 10 - idx
                  }} data-id="element-130">
                          <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tool}.svg`} alt={tool} className="w-3.5 h-3.5 object-contain" onError={e => {
                      ;
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }} data-id="element-131" />
                        </div>)}
                    </div>
                    <div className="w-px h-3 bg-[#EAE4D9]" data-id="element-132" />
                    <div className="flex items-center gap-1.5" data-id="element-133">
                      <span className="relative flex h-1.5 w-1.5" data-id="element-134">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-135"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" data-id="element-136"></span>
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-px" data-id="element-137">
                        Active
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right Column: Details */}
              <div className="w-full md:w-[55%] flex flex-col items-start" data-id="element-138">
                <motion.h3 initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              delay: 0.2,
              duration: 0.4
            }} className="text-[28px] sm:text-4xl md:text-5xl lg:text-6xl font-title font-bold text-[#2B211A] leading-[1.1] mb-4 md:mb-6 max-w-2xl" data-id="element-139">
                  {expandedPersona.headline}
                </motion.h3>

                {/* Working on now — concurrent live tasks she is handling this moment */}
                <motion.span initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              delay: 0.32,
              duration: 0.4
            }} className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-[#EA580C] mb-3 md:mb-4" data-id="element-140">
                  {isTasklet ? `Active tasks for Giga ${expandedPersona.role}` : `Handed off to Giga by your ${expandedPersona.role}`}
                </motion.span>

                <div className="flex flex-col gap-2.5 md:gap-3 w-full max-w-xl" data-id="element-141">
                  {[expandedPersona.doing, ...expandedPersona.examples].map((task, i) => <LiveTaskRow key={i} task={task} index={i} data-id="element-142" />)}
                </div>

                {/* On-thesis takeaway — aligned left to match the task list above it */}
                <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} transition={{
              delay: 0.5,
              duration: 0.4
            }} className="mt-auto pt-6 md:pt-8 self-start text-left text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-xl" data-id="element-143">
                  {isTasklet ? expandedPersona.taskletOutcome : expandedPersona.outcome}
                </motion.p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}